import { useQuery } from "@tanstack/react-query";
import styled from "styled-components";
import { withAppHOC } from "../../hoc";
import { StyledContentContainer } from "../../components/styled";
import { MessageBox } from "../../components/FormElements";
import Loader from "../../components/Loader/Loader";
import PageHeader from "../../components/PageHeader/PageHeader";
import API from "../../api";
import { RESPONSE_STATUSES } from "../../constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faCalculator,
  faEnvelope,
  faLocationDot,
  faPhoneAlt,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";

/* ------------------ CARD GRADIENTS ------------------ */
const cardGradients = [
  "linear-gradient(135deg, #1f1c2c, #928dab)",
  "linear-gradient(135deg, #0f2027, #2c5364)",
  "linear-gradient(135deg, #42275a, #734b6d)",
  "linear-gradient(135deg, #0f2027, #203a43)",
  "linear-gradient(135deg, #4b6cb7, #182848)",
];

/* ------------------ STYLED COMPONENTS ------------------ */
const Container = styled(StyledContentContainer)`
  padding: 30px 20px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 20px;
`;

const Card = styled.div`
  background: ${({ index }) => cardGradients[index % cardGradients.length]};
  padding: 20px 25px;
  border-radius: 12px;
  display: flex;
  align-items: flex-start;
  gap: 15px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(0, 0, 0, 0.2);
  }

  svg.icon {
    font-size: 32px;
    color: #ffffffaa;
    flex-shrink: 0;
  }

  .content {
    flex: 1;

    span.label {
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      color: #ffffffcc;
      margin-bottom: 4px;
      display: block;
    }

    p.value {
      font-size: 16px;
      font-weight: 500;
      color: #ffffff;
      margin: 0;
      line-height: 1.4;
      word-break: break-word;
      display: flex;
      align-items: center;
      gap: 6px;

      .copy-icon {
        font-size: 14px;
        color: #ffffffaa;
        cursor: pointer;

        &:hover {
          color: #fff;
        }
      }
    }
  }
`;

const TermsCard = styled.div`
  background: #ffffff;
  padding: 25px 30px;
  margin-top: 25px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  line-height: 1.6;

  h3 {
    margin-bottom: 16px;
    font-size: 20px;
    font-weight: 700;
    color: #222;
    border-bottom: 2px solid #f0f0f0;
    padding-bottom: 8px;
  }

  h4 {
    margin: 12px 0 8px 0;
    font-size: 16px;
    font-weight: 600;
    color: #444;
  }

  ul, ol {
    padding-left: 20px;
    margin: 6px 0 12px 0;
  }

  li {
    margin-bottom: 6px;
  }

  .important {
    background: #fff4e5;
    border-left: 4px solid #ffa726;
    padding: 8px 12px;
    margin: 8px 0;
    font-weight: 500;
  }

  a {
    color: #1e88e5;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
      color: #1565c0;
    }
  }
`;

/* ------------------ API FETCH ------------------ */
const fetchFirmDetails = async () => {
  const response = await API.get("/meta/firm-details");
  return response.data;
};

/* ------------------ STATIC TERMS ------------------ */
const staticSellerTerms = [
  {
    heading: "Invoice Rules",
    items: [
      "E-invoice is mandatory for clients whose sales exceed ₹5 crore.",
      "Upload the last month’s Sales Tax Invoice with your stamp and signature.",
      "Last date for uploading invoices: 7th of every month.",
      "Upload invoices in GSTR-1 before the 11th of every month (GST Rule 36(4)).",
      "Non-compliance may result in wallet block or suspension of further transactions."
    ]
  },
  {
    heading: "Complaint and Pending Rules",
    items: [
      "Complaint TAT: 1 working hour; Pending TAT: 15 minutes.",
      "Strict adherence to TAT is required; violations may result in reversals or account deactivation.",
      "Refunds after complaints are held according to the grading system.",
      "If fake success increases, the company may block your wallet and halt transactions.",
      "Re-opened complaints must provide proper proof (e.g., ROBO screenshot); otherwise, the recharge amount may be blocked."
    ]
  },
  {
    heading: "Redeem Rules",
    items: [
      "Wallet redemption is allowed according to the grading system.",
      "Only accounts verified with the firm name are eligible for redemption.",
      "Payout API is used for redemption; banking issues may delay processing (48–72 working hours).",
      "Bank account changes will apply to future redemptions."
    ]
  }
];

const staticBuyerTerms = [
  {
    heading: "General Rules",
    items: [
      "There is no registration fee at www.multiexchange.in",
      "Only prepaid and DTH E-top stock buy/sale is allowed on the platform.",
      "Only GST-registered persons can sell.",
      "No setup cost for Seller API integration.",
      "Live data on the website will remain available for 60 days only."
    ]
  },
  {
    heading: "GST & KYC Compliance",
    items: [
      "Update your GST Number correctly during registration or KYC updates to receive valid GST invoices.",
      "Failing to update GST may result in B2C classification, preventing issuance of a GST invoice."
    ]
  },
  {
    heading: "Margin & Service Rules",
    items: [
      "Minimum commission must be set on purchase margin; system provides the maximum margin available in stock.",
      "24/7 service is provided with unlimited E-top values."
    ]
  },
  {
    heading: "Complaint & Pending Rules",
    items: [
      "Complaints must be registered via the portal within 5 days of the transaction.",
      "Complaint registration occurs 5 minutes after the transaction.",
      "Wrong-to-right number transfers or DTH reversals are subject to specific rules."
    ]
  },
  {
    heading: "DTH Rules",
    items: [
      "DISH TV: Only wrong-to-right transfer is allowed within 48 hours.",
      "Videocon D2H: Only wrong-to-right transfer is allowed within 48 hours.",
      "TATA SKY: Only reversal is allowed within 48 hours.",
      "AIRTEL DTH: No reversal or right-number transfer allowed.",
      "SUN TV: No reversal or right-number transfer allowed."
    ]
  },
  {
    heading: "Wallet Top-Up Rules",
    items: [
      "Auto-update requires a minimum payment transfer of ₹5,000.",
      "If auto-update fails, submit a payment request with the correct UTR."
    ]
  },
  {
    heading: "Support",
    items: [
      "Amounts transferred to a normal account are credited to your wallet within 1 hour.",
    ]
  }
];

/* ------------------ MAIN COMPONENT ------------------ */
const FirmDetails = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["firm_details"],
    queryFn: fetchFirmDetails,
  });

  const firm = data?.data || {};

  const renderCopyIcon = (text) => (
    <FontAwesomeIcon
      icon={faCopy}
      className="copy-icon"
      onClick={() => navigator.clipboard.writeText(text)}
      title="Copy"
    />
  );

  if (isLoading) {
    return (
      <>
        <PageHeader title="Company Profile" />
        <Container>
          <Loader isLoading />
        </Container>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader title="Company Profile" />
        <Container>
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={error.message}
          />
        </Container>
      </>
    );
  }

  const details = [
    { icon: faBuilding, label: "Legal Name", value: firm.firmName },
    { icon: faLocationDot, label: "Head Office", value: firm.firmAddress },
    { icon: faCalculator, label: "GST Number", value: firm.gstNumber },
    { icon: faPhoneAlt, label: "Support Number", value: firm.supportNumber },
    { icon: faEnvelope, label: "Support Email", value: firm.supportEmail },
  ];

  return (
    <>
      <PageHeader title="Company Profile" />
      <Container>
        {/* -------- Basic Details Cards -------- */}
        <Grid>
          {details.map((item, index) => (
            <Card key={index} index={index}>
              <FontAwesomeIcon icon={item.icon} className="icon" />
              <div className="content">
                <span className="label">{item.label}</span>
                <p className="value">
                  {item.value || "-"}{" "}
                  {item.value &&
                    (item.label === "GST Number" ||
                      item.label.includes("Email")) &&
                    renderCopyIcon(item.value)}
                </p>
              </div>
            </Card>
          ))}
        </Grid>

        {/* -------- Seller Terms -------- */}
        <TermsCard>
          <h3>Seller Terms & Conditions</h3>
          {staticSellerTerms.map((section, i) => (
            <div key={i}>
              <h4>{section.heading}</h4>
              <ul>
                {section.items.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </TermsCard>

        {/* -------- Buyer Terms -------- */}
        <TermsCard>
          <h3>Buyer Terms & Conditions</h3>
          {staticBuyerTerms.map((section, i) => (
            <div key={i}>
              <h4>{section.heading}</h4>
              <ul>
                {section.items.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </TermsCard>
      </Container>
    </>
  );
};

export default withAppHOC(FirmDetails);
